import { useServerFetch } from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { LoadingSpinnerPresets } from 'dg-web-shared/common/components/material-ui/PresetLoadingSpinner.tsx';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import { Outlet, useNavigate } from 'react-router-dom';
import { MainContent } from '../../shared-mui-components/MainContent';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import { EmptyResults } from '../../shared-mui-components/ListMessages.tsx';
import { SeparationBar } from '../../layout/components/SeparationBar.tsx';
import { OperatorAsyncLoadedSection } from '../../app/components/OperatorAsyncLoadedSection.tsx';
import { MaterialHeader } from '../../layout/components/MaterialHeader.tsx';
import { MaterialTitle } from '../../layout/components/MaterialTitle.tsx';
import { Schedule } from '@mui/icons-material';
import { TariffCompatibility } from 'taxomex-shared/tariff/models/TariffCompatibility.ts';

interface Tariff {
    id: number;
    name: string;
    compatibility: TariffCompatibility;
    zonesWithTariffInUse: number;
}

export function TariffsOverview() {
    const [tariffsListState] = useServerFetch<Tariff[], object>(
        () => ({
            url: `/ui-api/operator-account/tariff/list`,
        }),
        {},
    );

    return (
        <MainContent>
            <MaterialHeader editMode={false}>
                <MaterialTitle
                    Icon={Schedule}
                    title={
                        <Localized
                            de="Tarife"
                            fr="Tarifs"
                            it="Tariffe"
                            en="Tariffs"
                        />
                    }
                />
            </MaterialHeader>
            <SeparationBar />
            <OperatorAsyncLoadedSection
                requestState={tariffsListState}
                pendingLoaderPreset={
                    LoadingSpinnerPresets.FillAllSpaceAndCenter
                }
                render={(tariffsList: Tariff[]) => {
                    if (tariffsList.length === 0) {
                        return <EmptyResults hasFilter={false} />;
                    }
                    return (
                        <TableContainer>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            <Localized
                                                de="Bezeichnung"
                                                fr="Description"
                                                it="Descrizione"
                                                en="Description"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Localized
                                                de="Typ"
                                                fr="Type"
                                                it="Tipo"
                                                en="Type"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Localized
                                                de="Verwendungen"
                                                fr="Utilisations"
                                                it="Utilizzi"
                                                en="Uses"
                                            />
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {tariffsList.map(tariff => (
                                        <TariffRow
                                            key={tariff.id}
                                            tariff={tariff}
                                        />
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    );
                }}
            />

            <Outlet />
        </MainContent>
    );
}

function TariffRow({ tariff }: { tariff: Tariff }) {
    const navigate = useNavigate();
    return (
        <TableRow
            hover
            sx={{ cursor: 'pointer' }}
            onClick={() => navigate(`${tariff.id}`)}
        >
            <TableCell>{tariff.name}</TableCell>
            <TableCell>
                {getTariffCompatibilityTranslation(tariff.compatibility)}
            </TableCell>
            <TableCell>
                <TariffUsesTranslation
                    compatibility={tariff.compatibility}
                    zonesWithTariffInUse={tariff.zonesWithTariffInUse}
                />
            </TableCell>
        </TableRow>
    );
}

function getTariffCompatibilityTranslation(
    compatibility: TariffCompatibility,
): string {
    switch (compatibility) {
        case TariffCompatibility.BARRIER_GATE:
            return 'Offstreet';
        case TariffCompatibility.ZONE_ENFORCED_DIGITAL:
        case TariffCompatibility.ZONE_ENFORCED_DIGITAL_AND_TOMECO:
            return 'Onstreet';
    }
}

function TariffUsesTranslation({
    compatibility,
    zonesWithTariffInUse,
}: {
    compatibility: TariffCompatibility;
    zonesWithTariffInUse: number;
}) {
    if (zonesWithTariffInUse === 0) {
        return <Localized de="Keine" fr="Aucune" it="Nessuno" en="None" />;
    }

    if (zonesWithTariffInUse === 1) {
        switch (compatibility) {
            case TariffCompatibility.BARRIER_GATE:
                return (
                    <Localized
                        de="1 Parking"
                        fr="1 parking"
                        it="1 parcheggio"
                        en="1 parking"
                    />
                );
            case TariffCompatibility.ZONE_ENFORCED_DIGITAL:
            case TariffCompatibility.ZONE_ENFORCED_DIGITAL_AND_TOMECO:
                return (
                    <Localized
                        de="1 Zone"
                        fr="1 zone"
                        it="1 zona"
                        en="1 zone"
                    />
                );
        }
    }

    switch (compatibility) {
        case TariffCompatibility.BARRIER_GATE:
            return (
                <Localized
                    de={`${zonesWithTariffInUse} Parkings`}
                    fr={`${zonesWithTariffInUse} parkings`}
                    it={`${zonesWithTariffInUse} parcheggi`}
                    en={`${zonesWithTariffInUse} parkings`}
                />
            );
        case TariffCompatibility.ZONE_ENFORCED_DIGITAL:
        case TariffCompatibility.ZONE_ENFORCED_DIGITAL_AND_TOMECO:
            return (
                <Localized
                    de={`${zonesWithTariffInUse} Zonen`}
                    fr={`${zonesWithTariffInUse} zones`}
                    it={`${zonesWithTariffInUse} zone`}
                    en={`${zonesWithTariffInUse} zones`}
                />
            );
    }
}
